import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/use-language";

const Hero = () => {
  const { t, localePath } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden gradient-hero">
      {/* Animated background elements — heavy blur/animations disabled on mobile to avoid GPU jank */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="hidden md:block absolute top-1/4 -left-32 w-96 h-96 bg-gold/20 rounded-full blur-[100px] animate-float" />
        <div className="hidden md:block absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gold/15 rounded-full blur-[120px] animate-float" style={{ animationDelay: "2s" }} />
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-navy-light/30 rounded-full blur-[150px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        <div className="hidden md:block absolute top-20 right-20 w-4 h-4 bg-gold/40 rounded-full animate-float" style={{ animationDelay: "1s" }} />
        <div className="hidden md:block absolute bottom-40 left-20 w-6 h-6 bg-gold/30 rounded-full animate-float" style={{ animationDelay: "3s" }} />
        <div className="hidden md:block absolute top-40 left-1/4 w-3 h-3 bg-white/20 rounded-full animate-float" style={{ animationDelay: "2s" }} />
      </div>

      <div className="container mx-auto px-6 lg:px-8 pt-32 pb-40 lg:pb-44 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-8 opacity-0 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5">
              <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
              <span className="text-sm text-white/80 font-medium">
                {t("Över 110 organisationer litar på Tiego", "Over 110 organizations trust Tiego")}
              </span>
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-normal text-white leading-[1.1] mb-8 opacity-0 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            {t("Vi förenklar", "We simplify")}{" "}
            <span className="gradient-text italic">{t("koncernredovisning", "group accounting")}</span>
            <br className="hidden sm:block" />
            {" "}{t("och leasinghantering", "and lease accounting")}
          </h1>

          {/* Subheadline */}
          <p className="text-center text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12 leading-relaxed opacity-0 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            {t(
              "Smarta systemlösningar för effektiv koncernredovisning och automatiserad leasinghantering enligt K3, IFRS och RKR R5.",
              "Smart system solutions for efficient group accounting and automated lease accounting under K3, IFRS and RKR R5."
            )}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 md:mb-14 opacity-0 animate-fade-in" style={{ animationDelay: "0.4s" }}>
            <Link to={localePath("/book-a-demo")}>
              <Button variant="hero" size="xl">
                {t("Boka demo", "Book a demo")}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-12 md:gap-16 max-w-2xl mx-auto py-4 opacity-0 animate-fade-in" style={{ animationDelay: "0.5s" }}>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-display text-white mb-3">110<span className="text-gold">+</span></div>
              <div className="text-sm text-white/50 font-medium">{t("Kunder", "Customers")}</div>
            </div>
            <div className="text-center border-x border-white/10">
              <div className="text-4xl md:text-5xl font-display text-white mb-3">762</div>
              <div className="text-sm text-white/50 font-medium">{t("Användare", "Users")}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-display text-white mb-3">2</div>
              <div className="text-sm text-white/50 font-medium">{t("System", "Systems")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom curve */}
      <div className="absolute bottom-[-1px] left-0 right-0 translate-y-px">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
